import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  CheckCircle2, 
  Workflow, 
  ShoppingCart, 
  Repeat,
  Zap,
  Database,
  Target,
  Home,
  Globe,
  Package
} from 'lucide-react';

const MultichannelVertrieb = () => {
  return (
    <div className="page-container">
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Target className="h-6 w-6 text-primary" />
              <span className="font-bold text-xl">Daniel Leismann</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <Home className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-accent">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Multichannel-Vertrieb</span>
          </div>
        </div>
      </div>

      <section className="hero-section-service">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Banner Image */}
          <div className="mb-8 rounded-xl overflow-hidden" style={{maxHeight: '200px'}}>
            <img 
              src="https://images.pexels.com/photos/230544/pexels-photo-230544.jpeg" 
              alt="Multichannel E-Commerce" 
              className="w-full h-48 object-cover"
            />
          </div>
          
          {/* Content Grid */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <h1 className="service-page-title">
                Multichannel-Vertrieb – Amazon, eBay, Otto zentral verwalten
              </h1>
              <p className="service-page-subtitle">
                Verkaufen Sie auf Amazon, eBay, Otto und mehr – zentral gesteuert aus einem System. Ich synchronisiere Bestände, Preise und Bestellungen automatisch über alle Kanäle hinweg. Keine doppelte Arbeit mehr.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/#kontakt">
                  <Button size="lg" className="cta-button-large">
                    Kostenlose Erstanalyse
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Alle Kanäle synchron</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Automatische Updates</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-accent" />
                  <span>Zentrale Verwaltung</span>
                </div>
              </div>
            </div>
            <div className="md:col-span-1">
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leismann-digital/artifacts/52t5d2l5_daniel_leismann.jpg" 
                  alt="Daniel Leismann" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="section-title text-center mb-12">Multichannel-Leistungen</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Workflow className="h-6 w-6" />
                </div>
                <h3 className="service-title">JTL-Wawi Integration</h3>
                <p className="service-description">
                  Zentrale Warenwirtschaft mit automatischer Synchronisation zu Amazon, eBay, Otto und eigenem Shop.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Repeat className="h-6 w-6" />
                </div>
                <h3 className="service-title">Bestands-Synchronisation</h3>
                <p className="service-description">
                  Echtzeitabgleich von Lagerbeständen über alle Verkaufskanäle – kein Überverkauf mehr.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <ShoppingCart className="h-6 w-6" />
                </div>
                <h3 className="service-title">Auftragsabwicklung</h3>
                <p className="service-description">
                  Automatische Bestellimport, zentrale Auftragsverarbeitung und Versandlabel-Erstellung.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Database className="h-6 w-6" />
                </div>
                <h3 className="service-title">Produktdaten-Management</h3>
                <p className="service-description">
                  Zentrale Pflege von Artikeldaten, Preisen, Bildern und Beschreibungen für alle Kanäle.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Zap className="h-6 w-6" />
                </div>
                <h3 className="service-title">Preis-Synchronisation</h3>
                <p className="service-description">
                  Automatische Preisanpassungen über alle Plattformen hinweg – individuell oder zentral.
                </p>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardContent className="p-6">
                <div className="service-icon">
                  <Package className="h-6 w-6" />
                </div>
                <h3 className="service-title">Versand-Integration</h3>
                <p className="service-description">
                  DHL, DPD, Hermes – automatische Label-Erstellung und Tracking-Benachrichtigung.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="visual-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title-dark">Unterstützte Plattformen</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8">
            <div className="text-white">
              <Globe className="h-12 w-12 mx-auto mb-2 text-accent" />
              <p className="font-semibold">Amazon</p>
            </div>
            <div className="text-white">
              <Globe className="h-12 w-12 mx-auto mb-2 text-accent" />
              <p className="font-semibold">eBay</p>
            </div>
            <div className="text-white">
              <Globe className="h-12 w-12 mx-auto mb-2 text-accent" />
              <p className="font-semibold">Otto</p>
            </div>
            <div className="text-white">
              <Globe className="h-12 w-12 mx-auto mb-2 text-accent" />
              <p className="font-semibold">Eigener Shop</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="section-title">Bereit für echtes Multichannel?</h2>
          <p className="section-subtitle mb-8">
            Lassen Sie uns Ihre Verkaufskanäle professionell synchronisieren.
          </p>
          <Link to="/#kontakt">
            <Button size="lg" className="cta-button-large">
              Jetzt kostenlose Erstanalyse anfragen
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Daniel Leismann. Alle Rechte vorbehalten.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MultichannelVertrieb;